import { useCallback, useEffect, useRef, useState } from "react";
import { startCompanionLlmStream } from "../services/companionGraphqlSseClient";
import { AttachmentInput } from "../graphql/generated/graphql";

type StreamCompletionEvent = { chatId?: string; messageId?: string };

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  attachment?: AttachmentInput;
  timeZone?: string;
  timestamp?: string;
  userMessageId?: string;
  isEditReplay?: boolean;
  anchorMessageId?: string;
  branchId?: string | null;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onError?: (payload: { error: Error }) => void;
  onChatRenamed?: (payload: { chatId: string; name: string }) => void;
}
export function useCompanionLlmStream() {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const { onComplete, onError, onChunk, onChatRenamed, timeZone, timestamp, ...streamOptions } = options;

    const resolvedTimeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const resolvedTimestamp = timestamp ?? new Date().toISOString();

    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setIsStreaming(true);

    let accumulatedText = "";
    let completionEvent: StreamCompletionEvent | undefined;

    try {
      await startCompanionLlmStream(
        question,
        controller,
        {
          onChunk: (event) => {
            if (event.__typename === "LLMStreamMessageDeltaEvent") {
              const delta = event.delta;
              if (delta?.__typename === "LLMStreamTextBlock" && delta.text) {
                accumulatedText += delta.text;
                onChunk?.({
                  text: delta.text,
                  messageId: event.messageId,
                });
              }
            }

            if (event.__typename === "LLMStreamMessageCompletedEvent") {
              completionEvent = {
                chatId: event.message?.chatId,
                messageId: event.message?.id,
              };
              setIsStreaming(false);
              onComplete?.({ event: completionEvent, text: accumulatedText });
            }

            if (event.__typename === "LLMStreamChatRenamedEvent") {
              onChatRenamed?.({
                chatId: event.chatId,
                name: event.name,
              });
            }

            if (event.__typename === "LLMStreamErrorEvent") {
              const message = event.errorMessage || "LLM stream error";
              setIsStreaming(false);
              onError?.({ error: new Error(message) });
            }
          },
          onError: (error) => {
            setIsStreaming(false);
            onError?.({ error });
          },
          onComplete: () => {
            setIsStreaming(false);
          },
        },
        {
          ...streamOptions,
          timeZone: resolvedTimeZone,
          timestamp: resolvedTimestamp,
        }
      );
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      setIsStreaming(false);
      onError?.({ error: err instanceof Error ? err : new Error(String(err)) });
      throw err;
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsStreaming(false);
  }, []);

  return { startStream, cancelStream, isStreaming };
}

export default useCompanionLlmStream;
